import {
  GiftCouponShareStatus,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';

// === 쿠폰 공유 상태 ===

const COUPON_SHARE_STATUS_STYLE: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: 'bg-gray-100 text-gray-600',
  [GiftCouponShareStatus.Shared]: 'bg-green-100 text-green-700',
  [GiftCouponShareStatus.Viewed]: 'bg-blue-100 text-blue-700',
};

const COUPON_SHARE_STATUS_LABEL: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: '미전달',
  [GiftCouponShareStatus.Shared]: '전달완료',
  [GiftCouponShareStatus.Viewed]: '확인됨',
};

export const getCouponShareStatusStyle = (status: GiftCouponShareStatus) =>
  COUPON_SHARE_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600';

export const getCouponShareStatusLabel = (status: GiftCouponShareStatus) =>
  COUPON_SHARE_STATUS_LABEL[status] ?? status;

// === 배송 상태 ===

const DELIVERY_STATUS_STYLE: Record<GiftDeliveryStatus, string> = {
  [GiftDeliveryStatus.WaitingAddress]: 'bg-amber-100 text-amber-700',
  [GiftDeliveryStatus.AddressSubmitted]: 'bg-blue-100 text-blue-700',
  [GiftDeliveryStatus.Preparing]: 'bg-indigo-100 text-indigo-700',
  [GiftDeliveryStatus.Shipping]: 'bg-purple-100 text-purple-700',
  [GiftDeliveryStatus.Delivered]: 'bg-green-100 text-green-700',
  [GiftDeliveryStatus.Canceled]: 'bg-red-100 text-red-700',
};

const DELIVERY_STATUS_LABEL: Record<GiftDeliveryStatus, string> = {
  [GiftDeliveryStatus.WaitingAddress]: '배송지 입력 대기',
  [GiftDeliveryStatus.AddressSubmitted]: '배송지 입력 완료',
  [GiftDeliveryStatus.Preparing]: '상품 준비중',
  [GiftDeliveryStatus.Shipping]: '배송중',
  [GiftDeliveryStatus.Delivered]: '배송 완료',
  [GiftDeliveryStatus.Canceled]: '취소',
};

export const getDeliveryStatusStyle = (status: GiftDeliveryStatus) =>
  DELIVERY_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600';

export const getDeliveryStatusLabel = (status: GiftDeliveryStatus) =>
  DELIVERY_STATUS_LABEL[status] ?? status;

// === 배송 공유 상태 ===

const DELIVERY_SHARE_STATUS_STYLE: Record<GiftDeliveryShareStatus, string> = {
  [GiftDeliveryShareStatus.NotShared]: 'bg-gray-100 text-gray-600',
  [GiftDeliveryShareStatus.Shared]: 'bg-green-100 text-green-700',
  [GiftDeliveryShareStatus.Viewed]: 'bg-blue-100 text-blue-700',
};

const DELIVERY_SHARE_STATUS_LABEL: Record<GiftDeliveryShareStatus, string> = {
  [GiftDeliveryShareStatus.NotShared]: '미전달',
  [GiftDeliveryShareStatus.Shared]: '전달완료',
  [GiftDeliveryShareStatus.Viewed]: '확인됨',
};

export const getDeliveryShareStatusStyle = (status: GiftDeliveryShareStatus) =>
  DELIVERY_SHARE_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600';

export const getDeliveryShareStatusLabel = (status: GiftDeliveryShareStatus) =>
  DELIVERY_SHARE_STATUS_LABEL[status] ?? status;

export const SHARE_GUIDE_DISMISSED_KEY = 'gift-coupon-share-guide-dismissed';

// === Pending Confirm (전달 확인 대기) 상태 localStorage 관리 ===

const PENDING_CONFIRM_KEY = 'gift-pending-confirm';

/**
 * 스키마 버전.
 * - v1: channel 이 UI 전용 소문자 union (`'sms' | 'kakao' | 'other'`) — 더 이상 사용 안 함
 * - v2: channel 이 백엔드 ShareChannel enum (`'KakaoTalk' | 'SMS' | 'Link' | 'Other'`)
 *
 * 구버전 엔트리는 매칭 로직에서 단순 무시하고 자연스럽게 만료시킨다 (영향 범위 작음).
 */
const PENDING_CONFIRM_VERSION = 2;

export interface PendingConfirmEntry {
  v: typeof PENDING_CONFIRM_VERSION;
  type: 'coupon' | 'delivery';
  itemId: string;
  seq: number;
  channel: ShareChannel;
  orderId: string;
  timestamp: number;
}

type PendingConfirmEntryInput = Omit<PendingConfirmEntry, 'v'>;

export function savePendingConfirm(entry: PendingConfirmEntryInput): void {
  try {
    const existing = loadPendingConfirms();
    const filtered = existing.filter((e) => e.itemId !== entry.itemId);
    filtered.push({ ...entry, v: PENDING_CONFIRM_VERSION });
    localStorage.setItem(PENDING_CONFIRM_KEY, JSON.stringify(filtered));
  } catch {
    // localStorage 접근 실패 무시
  }
}

export function loadPendingConfirms(): PendingConfirmEntry[] {
  try {
    const raw = localStorage.getItem(PENDING_CONFIRM_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // 구버전 엔트리(v 가 없거나 다름)는 무시 — channel enum 변경으로 호환 불가
    return parsed.filter(
      (e): e is PendingConfirmEntry =>
        e && typeof e === 'object' && e.v === PENDING_CONFIRM_VERSION,
    );
  } catch {
    return [];
  }
}

export function loadPendingConfirmForOrder(orderId: string): PendingConfirmEntry | null {
  const all = loadPendingConfirms();
  return all.find((e) => e.orderId === orderId) ?? null;
}

export function removePendingConfirm(itemId: string): void {
  try {
    const existing = loadPendingConfirms();
    const filtered = existing.filter((e) => e.itemId !== itemId);
    localStorage.setItem(PENDING_CONFIRM_KEY, JSON.stringify(filtered));
  } catch {
    // localStorage 접근 실패 무시
  }
}

// === Pending Duplicate Suspicion (다건수신 의심 → 카톡 확인 후 후속 처리 대기) ===
//
// 흐름:
//   1) ShareConfirmModal 다건수신 경고에서 "확인하러 가기" 클릭
//      → 이 엔트리를 localStorage 에 저장 + 모달 닫기 (사용자 카톡 이동)
//   2) 사용자가 페이지로 복귀하면 (페이지 진입 또는 focus 이벤트)
//      → loadPendingDuplicateSuspicionForOrder 로 복원
//      → SuspicionConfirmModal 자동 노출
//   3) 모달에서 "잘 보낸 거였어요" / "중복 → 다시 보내기" / "중복 → 나중에" 등
//      어떤 분기를 타든 마지막에 removePendingDuplicateSuspicion 으로 정리
//
// pending-confirm 과 별도 키를 쓰는 이유:
//   - pending-confirm 은 "전달 직후 확인" 단계
//   - pending-duplicate-suspicion 은 "다건수신 의심 확인 후 후속" 단계
//   둘이 같은 itemId 에 동시에 존재할 수 있음 (의심 마크는 별도 라이프사이클)

const PENDING_DUPLICATE_SUSPICION_KEY = 'gift-pending-duplicate-suspicion';
const PENDING_DUPLICATE_SUSPICION_VERSION = 1;

export interface PendingDuplicateSuspicionConflict {
  seq: number;
  code: string;
}

export interface PendingDuplicateSuspicionEntry {
  v: typeof PENDING_DUPLICATE_SUSPICION_VERSION;
  type: 'coupon' | 'delivery';
  itemId: string;
  /** 의심된 쿠폰/배송의 순번 (메시지 표시용) */
  seq: number;
  orderId: string;
  /** 의심 시점에 매칭된 lastSharedKakaoHashChatId */
  hashChatId: string;
  /** 같은 hashChatId 로 전달된 다른 답례품들 (메시지 #N 라벨 생성용) */
  conflicts: PendingDuplicateSuspicionConflict[];
  timestamp: number;
}

type PendingDuplicateSuspicionInput = Omit<PendingDuplicateSuspicionEntry, 'v'>;

export function savePendingDuplicateSuspicion(
  entry: PendingDuplicateSuspicionInput,
): void {
  try {
    const existing = loadPendingDuplicateSuspicions();
    const filtered = existing.filter((e) => e.itemId !== entry.itemId);
    filtered.push({ ...entry, v: PENDING_DUPLICATE_SUSPICION_VERSION });
    localStorage.setItem(PENDING_DUPLICATE_SUSPICION_KEY, JSON.stringify(filtered));
  } catch {
    // localStorage 접근 실패 무시
  }
}

export function loadPendingDuplicateSuspicions(): PendingDuplicateSuspicionEntry[] {
  try {
    const raw = localStorage.getItem(PENDING_DUPLICATE_SUSPICION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is PendingDuplicateSuspicionEntry =>
        e && typeof e === 'object' && e.v === PENDING_DUPLICATE_SUSPICION_VERSION,
    );
  } catch {
    return [];
  }
}

export function loadPendingDuplicateSuspicionForOrder(
  orderId: string,
): PendingDuplicateSuspicionEntry | null {
  const all = loadPendingDuplicateSuspicions();
  return all.find((e) => e.orderId === orderId) ?? null;
}

/**
 * 같은 주문 내에서 여러 답례품이 의심 마크된 경우를 모두 반환한다.
 * (ex: 1번/3번 답례품을 같은 분께 보냈다 의심 → 둘 다 마크됨)
 *
 * - 의심 섹션 분류 hydrate 용 → 전부 가져와야 함
 * - 모달 자동 노출은 여전히 첫 entry 한 건만 (loadPendingDuplicateSuspicionForOrder)
 */
export function loadPendingDuplicateSuspicionsForOrder(
  orderId: string,
): PendingDuplicateSuspicionEntry[] {
  return loadPendingDuplicateSuspicions().filter((e) => e.orderId === orderId);
}

export function removePendingDuplicateSuspicion(itemId: string): void {
  try {
    const existing = loadPendingDuplicateSuspicions();
    const filtered = existing.filter((e) => e.itemId !== itemId);
    localStorage.setItem(PENDING_DUPLICATE_SUSPICION_KEY, JSON.stringify(filtered));
  } catch {
    // localStorage 접근 실패 무시
  }
}
